import React from 'react';
import { Button } from './ui/button';
import { Check, Star } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from './ui/card';
import { Badge } from './ui/badge';

const Pricing = () => {
  const plans = [
    {
      name: 'Free',
      price: '0',
      period: 'forever',
      storage: '100GB',
      features: [
        'Invite-only access',
        'Secure file sharing',
        'Basic phone expansion',
        'Cross-platform sync',
        'Earn up to 1TB free via referrals',
        '128-bit encryption'
      ],
      cta: 'View Plans',
      popular: false
    },
    {
      name: 'Premium',
      price: '0.5',
      period: 'per GB/month',
      storage: 'Pay as you grow',
      features: [
        'Everything in Free',
        'Unlimited devices',
        'Priority support',
        'Advanced encryption',
        'Custom expiration on shares',
        'GoogleDrive, OneDrive, iCloud Migration Support',
      ],
      cta: 'View Plans',
      popular: true
    }
  ];

  const viewPlans = () => {
    window.location.href = 'https://api.drivault.com/pages/pricing.php';
  };

  return (
    <section id="pricing" className="py-20 bg-gradient-to-br from-gray-50 via-white to-emerald-50/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Simple, <span className="text-[#40E095]">Transparent</span> Pricing
          </h2>
          <p className="text-lg text-gray-600">
            Start free with 100GB. Scale as you grow. No hidden fees.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {plans.map((plan, index) => (
            <Card 
              key={index}
              className={`relative ${
                plan.popular 
                  ? 'border-2 border-[#40E095] shadow-2xl scale-105' 
                  : 'border-2 border-gray-200 hover:border-[#40E095]/50'
              } transition-all duration-300 hover:shadow-xl`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <Badge className="bg-[#40E095] text-white px-4 py-1 text-sm font-semibold">
                    <Star size={14} className="inline mr-1" />
                    RECOMMENDED
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center pb-8 pt-8">
                <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                  {plan.name}
                </CardTitle>
                <div className="mt-4">
                  <span className="text-5xl font-bold text-gray-900">
                    {plan.price === '0' ? 'Free' : `₹${plan.price}`}
                  </span>
                  {plan.price !== '0' && (
                    <span className="text-gray-600 ml-2">{plan.period}</span>
                  )}
                </div>
                <CardDescription className="text-lg font-semibold text-[#40E095] mt-2">
                  {plan.storage} Storage
                </CardDescription>
              </CardHeader>

              <CardContent>
                <ul className="space-y-4">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <div className="bg-[#40E095]/10 rounded-full p-1 mt-0.5">
                        <Check className="text-[#40E095]" size={16} />
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter className="pt-6">
                <Button 
                  onClick={viewPlans}
                  className={`w-full py-6 text-lg font-semibold transition-all ${
                    plan.popular
                      ? 'bg-[#40E095] hover:bg-[#36C981] text-white shadow-lg hover:shadow-xl hover:scale-105'
                      : 'bg-white hover:bg-[#40E095] text-[#40E095] hover:text-white border-2 border-[#40E095]'
                  }`}
                >
                  {plan.cta}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Additional Info */}
        <div className="text-center mt-12">
          <p className="text-gray-600">
            All plans include 128-bit encryption, cross-platform support, and secure sharing.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Enterprise plans available. <a href="#waitlist" className="text-[#40E095] hover:underline font-medium">Contact us</a> for custom solutions.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
